import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Modal, Row, Space, Spin, Typography } from 'antd';
const { Paragraph } = Typography;
import { useNavigate } from 'react-router-dom';
import NewClaimIcon from 'assets/images/home/new-claim.svg';
import noClaims from 'assets/images/no-claims.svg';
import ThreeItemColumn from 'components/threeItemColumn';
import ChevronRight from 'assets/images/home/chevron-right.svg';
// import ClaimTypeIcon from 'components/claimTypeIcon';
import { StyledTitle } from 'components/StyledTitle';
import ClaimBadge from './components/ClaimBadge';
import {
  useGetPolicyDrop,
  useGetClaimCIList,
  GetClaimCIListDetailResponse,
  OtherClaimItem,
  useGetClaimCIDetail,
} from 'all-in-one-shared-api';
import { useKeycloakReady } from 'hooks';
import OutpatientIcon from 'assets/images/claims/outpatient.svg';
// import { isLocaleEnglish } from 'utils/languageUtils';
// import { MarkdownTargetBlank } from 'components';
import { Markdown } from 'components';
import { PaddedUl } from 'components/markdown/components';

const Container = styled.div`
  padding: 40px;
  min-height: 400px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h2`
  margin-bottom: 24px;
  color: #333;
  font-size: 28px;
  font-weight: bold;
  text-align: left;
  padding: 0 24px;
`;

// 样式对齐 claims/QuickActions 中的 IconCard：固定尺寸、纵向布局、antd 卡片阴影与圆角
const Card = styled.div<{ disabled?: boolean }>`
  background: ${(props) => (props.disabled ? '#f5f5f5' : 'white')};
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px -2px rgba(0, 0, 0, 0.16), 0 3px 6px 0 rgba(0, 0, 0, 0.12), 0 5px 12px 4px rgba(0, 0, 0, 0.09);
  height: 13.75rem;
  max-width: 270px;
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;

// 与 IconCard 的 Description 保持一致：宽度 100%、主题色 var(--neutral-8)、无下边距
const Description = styled(Paragraph)`
  width: 100%;
  color: var(--neutral-8);
  margin-bottom: 0 !important;
`;

const SectionTitle = styled.h3`
  margin: 40px 0 24px 0;
  color: #333;
  font-size: 24px;
  font-weight: bold;
`;

const EmptyStateCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 60px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const EmptyIcon = styled.img`
  width: 120px;
  height: 120px;
  opacity: 0.5;
`;

const EmptyTitle = styled.h4`
  margin: 0;
  color: #333;
  font-size: 20px;
  font-weight: bold;
  text-align: center;
`;

const EmptyDescription = styled.p`
  margin: 0;
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  text-align: center;
  max-width: 600px;
  word-wrap: break-word;
`;

const KnowMoreLinkImage = styled.img`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const ClaimCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    transition: box-shadow 0.3s ease;
  }

  .top-desc {
    padding: 24px;
    cursor: pointer;
    display: flex;
    gap: 24px;
    align-items: flex-start;
  }

  .left-icon {
    width: 60px;
    height: 60px;
    flex-shrink: 0;
  }

  .center-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .tagging {
    margin: 0;
    color: #666;
    font-size: 17px;
    display: flex;
    gap: 8px;
    align-items: center;

    .tag-content {
      font-weight: bold;
      color: #333;
    }
  }

  .status {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    text-transform: uppercase;
    white-space: nowrap;
    width: fit-content;
    max-width: 100%;

    &.success {
      background-color: #f2fbe9;
      color: #549736;
    }

    &.failB {
      background-color: #fdeeec;
      color: #e9573d;
    }

    &.over {
      background-color: #fffbe6;
      color: #faad14;
    }
  }

  .show-status {
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 17px;
    font-weight: 500;
    display: inline-block;
  }

  .bottom-button {
    padding: 24px;
    border-top: 1px solid #f0f0f0;
  }
`;
const MarkdownTextWrapper = styled.div`
  font-size: 20px;
  color: rgba(0, 0, 0, 0.85);
  margin-bottom: 1em;
`;

const NoBulletUl = styled(PaddedUl)`
  list-style: none;
`;

const LearnMoreLink = styled.span`
  color: #c21b17;
  cursor: pointer;
`;

const StyledNoteModal = styled(Modal)`
  .ant-modal-body {
    height: max(50vh, 400px);
    overflow-y: auto;
  }
`;

const SubmitClaimsPlaceholder: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { keycloak } = useKeycloakReady();
  // const isEnglish = isLocaleEnglish(i18n.language);

  const getCausedByLabel = (value: string) => {
    const causedByMap: Record<string, string> = {
      Illness: t('life.submitClaims.illness'),
      Accident: t('life.submitClaims.accident'),
      疾病: t('life.submitClaims.illness'),
      意外: t('life.submitClaims.accident'),
    };
    return causedByMap[value] || value;
  };

  const [randoms, setRandoms] = useState(Math.random());
  const [randomsDt, setRandomsDt] = useState(Math.random());
  const [disabled, setDisabled] = useState(false);
  const [claimId, setClaimId] = useState<number>();
  const [pendingFlag, setPendingFlag] = useState<boolean>();

  const [claims, setClaims] = useState<Array<GetClaimCIListDetailResponse>>([]); // Sample data for demo
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  const [isLearnMoreModalVisible, setIsLearnMoreModalVisible] = useState(false);
  const showLearnMoreModal = () => setIsLearnMoreModalVisible(true);
  const closeLearnMoreModal = () => setIsLearnMoreModalVisible(false);

  const { shouldFetch: shouldFecthDrop, data: dropData } = useGetPolicyDrop('useGetPolicyDrop', keycloak.token || '');
  const { shouldFetch, data } = useGetClaimCIList('useGetClaimCIList' + randoms, keycloak.token || '');
  const { shouldFetch: shouldFetchDetail, data: detailData } = useGetClaimCIDetail(
    'useGetClaimCIDetail' + randomsDt,
    keycloak.token || '',
    claimId?.toString() || '',
  );

  useEffect(() => {
    setRandoms(Math.random());
    shouldFecthDrop(true);
    shouldFetch(true);
    // shouldFetchLearnMore(true);
  }, [shouldFecthDrop, shouldFetch]);
  useEffect(() => {
    if (!dropData) return;
    if (dropData.length == 0) setDisabled(true);
    else setDisabled(false);
  }, [dropData]);
  useEffect(() => {
    if (!data) return;
    setClaims(data);
    setLoading(false);
    shouldFetch(false);
  }, [data, shouldFetch]);
  useEffect(() => {
    if (claimId) {
      setDetailLoading(true);
      setRandomsDt(Math.random());
      shouldFetchDetail(true);
    }
  }, [claimId, shouldFetchDetail]);
  useEffect(() => {
    if (!detailData) return;
    const formData = {
      ...detailData,
      hospitalList: (detailData.hospitalList || []).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
      familMemberList: (detailData.familMemberList || []).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
      otherHospitalsList: (detailData.otherHospitalsList || []).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
      otherClaimList: (detailData.otherClaimList || [])
        .map((item: OtherClaimItem) => ({
          ...item,
          sumInsuredAmount: item.sumInsuredAmount ? Number(item.sumInsuredAmount) : undefined,
        }))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    };
    shouldFetchDetail(false);

    if (
      (detailData?.changeStatusCode === 'Pending' || detailData?.changeStatusCode === 'Declined&Pending') &&
      pendingFlag
    ) {
      navigate('/ClaimConfirmPage', {
        state: {
          formData,
          updateFiles: 'update',
        },
      });
    } else {
      navigate('/ClaimConfirmPage', {
        state: {
          formData,
          updateFiles: 'view',
        },
      });
    }
  }, [detailData, shouldFetchDetail, navigate, pendingFlag]);

  return (
    <Container>
      {detailLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <Spin size="large" />
        </div>
      )}
      <Title style={{ paddingLeft: 0 }}>{t('life.submitClaims.title')}</Title>
      <MarkdownTextWrapper>
        {/* <MarkdownTargetBlank>
          {t('life.submitClaims.secTitle', { url: isEnglish ? learnMoreData?.linkEn : learnMoreData?.linkTc })}
        </MarkdownTargetBlank> */}
        {t('life.submitClaims.secTitle')}[
        <LearnMoreLink onClick={showLearnMoreModal}>{t('life.submitClaims.secTitleLearnMore')}</LearnMoreLink>]
      </MarkdownTextWrapper>
      <Card disabled={disabled} onClick={() => navigate('/SubmitClaimFormPage')}>
        <img src={NewClaimIcon} alt="New Claim" style={{ width: '84px', height: '84px' }} />
        <StyledTitle level={5} color="var(--neutral-9)" fontWeight="600">
          {t('life.submitClaims.cardTitle')}
        </StyledTitle>
        <Description>{t('life.submitClaims.description')}</Description>
      </Card>

      <SectionTitle>{t('life.submitClaims.claimHistory')}</SectionTitle>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size="large" />
        </div>
      ) : claims.length === 0 ? (
        <EmptyStateCard>
          <EmptyIcon src={noClaims} alt="No Claims" />
          <EmptyTitle>{t('life.submitClaims.noClaimsTitle')}</EmptyTitle>
          <EmptyDescription>{t('life.submitClaims.noClaimsDescription')}</EmptyDescription>
        </EmptyStateCard>
      ) : (
        <Row gutter={[16, 16]}>
          {claims.map((claim, index) => (
            <ThreeItemColumn key={index}>
              <ClaimCard className="policy-card">
                <div
                  className="top-desc"
                  onClick={() => {
                    setClaimId(claim.id);
                    setPendingFlag(!(claim?.pendingUploadFlag?.toLowerCase() === 'yes'));
                    // Transform claim data to form-compatible format
                  }}
                >
                  <div style={{ width: '100%', display: 'flex', alignItems: 'flex-start' }}>
                    <Space align="start" style={{ flex: 1 }}>
                      {/* <ClaimTypeIcon claimType={claim.claimType} /> */}
                      <img src={OutpatientIcon} alt="" />
                      <Space direction="vertical" style={{ flex: 1 }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                          }}
                        >
                          <StyledTitle level={5}>{getCausedByLabel(claim.causedBy)}</StyledTitle>
                        </div>
                        <Typography.Text>{claim.policyNumber}</Typography.Text>
                        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                          {claim?.changeStatusCode && (
                            <ClaimBadge
                              code={claim.changeStatusCode}
                              status={claim.changeStatus}
                              statusTc={claim.changeStatusTc}
                              styles={{ marginRight: '5px' }}
                            />
                          )}
                          {claim?.changeStatusCode2 && (
                            <ClaimBadge
                              code={claim.changeStatusCode2}
                              status={claim.changeStatus2}
                              statusTc={claim.changeStatusTc2}
                            />
                          )}
                        </div>
                        <Typography.Text type="secondary">
                          {t('life.submitClaims.treatedOn', { date: claim.submissionDate })}
                        </Typography.Text>
                        <Typography.Text type="secondary">
                          {t('claims.referenceNo')}: {claim.bpmReferenceNo}
                        </Typography.Text>
                        {claim.claimNo && (
                          <Typography.Text type="secondary">
                            {t('claimDetails.claimNo')}: {claim.claimNo}
                          </Typography.Text>
                        )}
                      </Space>
                    </Space>
                    <KnowMoreLinkImage
                      src={ChevronRight}
                      alt="know-more-arrow"
                      className="more"
                      style={{ flexShrink: 0 }}
                    />
                  </div>
                </div>
              </ClaimCard>
            </ThreeItemColumn>
          ))}
        </Row>
      )}

      <StyledNoteModal
        title={t('life.submitClaims.secTitle')}
        open={isLearnMoreModalVisible}
        okText={t('common.ok')}
        cancelButtonProps={{ style: { display: 'none' } }}
        onOk={closeLearnMoreModal}
        onCancel={closeLearnMoreModal}
      >
        <Markdown components={{ ul: ({ node, ...props }) => <NoBulletUl {...props} /> }}>
          {t('life.submitClaims.learnMoreContent')}
        </Markdown>
      </StyledNoteModal>
    </Container>
  );
};

export default SubmitClaimsPlaceholder;
